import { Remark } from 'react-remark'
import rehypeRaw from 'rehype-raw'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeStringify from 'rehype-stringify'
import remarkParse from 'remark-parse'
import React from 'react'

import { Text } from '@wartek-id/text'
import { Link } from '@wartek-id/link'

import { IS_CONVERSATION_WITH_AI_MATH_FORMATTING_ENABLED } from 'configs/toggle'

import 'katex/dist/katex.min.css'

export const RenderMD = ({ children }) => {
  if (!children) {
    return ''
  }

  if (typeof children !== 'string') {
    return children
  }

  const preprocessLaTeX = (content: string) => {
    // Replace block-level LaTeX delimiters \[ \] with $$ $$
    const blockProcessedContent = content.replace(/\\\]|\\\[/gs, '$$')

    // Replace inline LaTeX delimiters \( \) with $ $
    const inlineProcessedContent = blockProcessedContent.replace(
      /\\\((.*?)\\\)/gs,
      (_, equation) => `$${equation}$`
    )
    return inlineProcessedContent
  }

  const preprocessedLaTeX = preprocessLaTeX(children)

  // parse children string, add \n before heading
  const parseHeadingNewLine = children.replace(
    /(#{1,3}\s.*?)(?=\s*#{1,3}\s|$)/g,
    '\n$1'
  )

  const parseOrderedListNewLine = parseHeadingNewLine.replace(
    /((?<=\s|^)\d+\.\s.*?)(?=\s\d+\.\s|$)/g,
    '\n$1'
  )

  const parseUnorderedListNewLine = parseOrderedListNewLine.replace(
    /(-\s[^-]*?)(?=-\s|$)/g,
    '\n$1'
  )

  return (
    <Remark
      //@ts-ignore
      remarkPlugins={
        IS_CONVERSATION_WITH_AI_MATH_FORMATTING_ENABLED
          ? [remarkParse, remarkMath]
          : []
      }
      //@ts-ignore
      rehypePlugins={
        IS_CONVERSATION_WITH_AI_MATH_FORMATTING_ENABLED
          ? [
              rehypeRaw,
              rehypeStringify,
              [
                rehypeKatex,
                {
                  displayMode: false,
                },
              ],
            ]
          : []
      }
      rehypeReactOptions={{
        components: {
          p: (props) => (
            <Text variant="body" className="mb-4" {...props}>
              {props.children}
            </Text>
          ),
          h1: (props) => (
            <Text variant="heading-lg" {...props}>
              {props.children}
            </Text>
          ),
          h2: (props) => {
            return (
              <Text variant="heading-md" {...props}>
                {props.children}
              </Text>
            )
          },
          h3: (props) => {
            return (
              <Text variant="heading-sm" {...props}>
                {props.children}
              </Text>
            )
          },
          h4: (props) => (
            <Text variant="subheading" {...props}>
              {props.children}
            </Text>
          ),
          h5: (props) => (
            <Text variant="subheading-sm" {...props}>
              {props.children}
            </Text>
          ),
          h6: (props) => (
            <Text variant="subheading-xs" {...props}>
              {props.children}
            </Text>
          ),
          a: (props) => <Link {...props}>{props.children}</Link>,
          img: (props) => <img {...props} alt="md-image" />,
          ul: (props) => (
            <ul className="ml-4" style={{ listStyle: 'outside' }} {...props}>
              <div className="ml-4">{props.children}</div>
            </ul>
          ),
          ol: (props) => {
            return (
              <ol {...props} className="ml-4" style={{ listStyle: 'auto' }}>
                <div className="ml-4">{props.children}</div>
              </ol>
            )
          },
          li: (props) => {
            return (
              <li {...props}>
                <Text variant="body" className="mb-4">
                  {Array.isArray(props.children) ? (
                    props.children.map((child, index) => (
                      <React.Fragment key={index}>{child}</React.Fragment>
                    ))
                  ) : (
                    <div className="pl-4">{props.children}</div>
                  )}
                </Text>
              </li>
            )
          },
        },
      }}
    >
      {IS_CONVERSATION_WITH_AI_MATH_FORMATTING_ENABLED
        ? preprocessedLaTeX
        : parseUnorderedListNewLine}
    </Remark>
  )
}
